import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface StatusBadgeProps {
  status: string;
  className?: string;
  color?: string; // Optional custom color (hex or hsl)
  variant?: 'default' | 'outlined';
}

const statusColorMap: Record<string, { bg: string; text: string; border: string }> = {
  pending: { bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/30' },
  processing: { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500/30' },
  shipped: { bg: 'bg-indigo-500/10', text: 'text-indigo-600', border: 'border-indigo-500/30' },
  delivered: { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/30' },
  cancelled: { bg: 'bg-red-500/10', text: 'text-red-600', border: 'border-red-500/30' },
  // Arabic status names
  'قيد الانتظار': { bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/30' },
  'قيد المعالجة': { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500/30' },
  'تم الشحن': { bg: 'bg-indigo-500/10', text: 'text-indigo-600', border: 'border-indigo-500/30' },
  'تم التسليم': { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/30' },
  'ملغي': { bg: 'bg-red-500/10', text: 'text-red-600', border: 'border-red-500/30' },
};

export function StatusBadge({ status, className, color, variant = 'outlined' }: StatusBadgeProps) {
  const statusColors = statusColorMap[status.toLowerCase()] || statusColorMap[status];
  
  // If custom color is provided, use inline styles
  if (color) {
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border',
          className
        )}
        style={{
          color: color,
          borderColor: color,
          backgroundColor: `${color}15`,
        }}
      >
        {status}
      </span>
    );
  }

  // Use predefined colors or fallback to default
  if (statusColors && variant === 'outlined') {
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border',
          statusColors.bg,
          statusColors.text,
          statusColors.border,
          className
        )}
      >
        {status}
      </span>
    );
  }

  // Fallback to default Badge
  return (
    <Badge
      variant="secondary"
      className={className}
    >
      {status}
    </Badge>
  );
}
